import {
  Badge,
  Box,
  Center,
  Collapse,
  Group,
  Pagination,
  Paper,
  Stack,
  Table,
  Text,
  Title,
  UnstyledButton,
  Col,
  Grid
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import React, { ChangeEvent, useState, useRef } from 'react';
import { ArrowLeft, X } from 'tabler-icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatNumberWithCommasAndDecimals } from '../../helpers/common.helpers';
import { formatDate3LetterMonth } from '../../helpers/date-convert';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import { IPaginationMeta } from '../../type/admin.types';
import { ErrorResponse } from '../../type/error-response.types';
import {
  BasicTransactionResponseCheckoutItem,
  BasicTransactionResponseDetails,
  RetrieveAllTransactionsRequest,
} from '../../type/retrieve-transaction.types';
import { SafeText } from '../safe-text/safe-text.component';
import './admin-transactions.styles.css';
import ExportButton from './export-button.component';
import RefundButton from '../buttons/refund-button.component';
import useAuth from '../../hooks/useAuth';
import { Roles } from '../../enums/roles-enum';
import { Modal, Button } from '@mantine/core';

const tableCols = {
  transactionId: 'Transaction ID',
  transactionNumber: 'Transaction number',
  merchantName: 'Merchant name',
  branchName: 'Branch name',
  posName: 'POS',
  customerName: 'Customer name',
  cardNumber: 'Card number',
  amount: 'Amount',
  dateCreated: 'Date created',
  signatureSource: 'Signature',
  posEntryMode: 'POS entry mode',
  notes: 'Notes',
  checkoutTransaction: 'Product Code/s',
  // Additional Columns//
  issuingBank: 'Issuing Bank',
  bankCountry: 'Bank Country',
  ipAddress: 'IP Address',
  refundStatus: 'Refund Status',
  payoutStatus: 'Payout Status',
  //Additional Columns //
  currency: 'Currency'
};
interface Props {
  transactionDetails: BasicTransactionResponseDetails[] | undefined;
  detailsPaginationData: IPaginationMeta | undefined;
  transactionDetailsRequest: RetrieveAllTransactionsRequest | undefined;
  setShowTransactionSummary: React.Dispatch<React.SetStateAction<boolean>>;
}

const groupByThree = (
  items: BasicTransactionResponseCheckoutItem[]
): BasicTransactionResponseCheckoutItem[][] => {
  const groupsOfThree = [];

  for (let i = 0; i < items.length; i += 3) {
    const group = items.slice(i, i + 3);
    groupsOfThree.push(group);
  }

  return groupsOfThree;
};

const GroupBadges = ({
  items,
}: {
  items: BasicTransactionResponseCheckoutItem[];
}) => {
  return (
    <Group noWrap>
      {items.map((product) => {
        return <Badge mr={3}>{product.code}</Badge>;
      })}
    </Group>
  );
};

const CollapseCode = ({
  items,
}: {
  items: BasicTransactionResponseCheckoutItem[];
}) => {
  const [collapse, setCollapse] = useState(true);

  return (
    <>
      <Stack sx={{ width: '100%' }}>
        <Collapse in={!collapse}>
          <Stack spacing='xs'>
            {groupByThree(items).map((groupItems) => {
              return <GroupBadges items={groupItems} />;
            })}
          </Stack>
        </Collapse>
        <UnstyledButton
          onClick={() => {
            setCollapse((prev) => !prev);
          }}
        >
          <Text color='#228be6' variant='link' sx={{ fontSize: 11 }}>
            {collapse ? 'See more' : 'See less'}
          </Text>
        </UnstyledButton>
      </Stack>
    </>
  );
};

function AdminTransactionsDetails(props: Props) {
  const { auth } = useAuth();
  const {
    transactionDetails,
    detailsPaginationData,
    transactionDetailsRequest,
    setShowTransactionSummary,
  } = props;
  const [paginationData] = useState(detailsPaginationData);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(transactionDetails);
  const axiosPrivate = useAxiosPrivate();

  const [openRow, setOpenRow] = useState<number | null>(null)
  const [isChecked, setIsChecked] = useState<string[]>([]);
  const [checkedAll, setCheckedAll] = useState(false);

  const [isBusy, setIsBusy] = useState(false);

  const refundAmountRef = useRef<HTMLInputElement>(null);

  const [opened, setOpened] = useState(false);
  const [refundTransaction, setRefundTransaction] = useState({
    transactionId: "",
    merchantName: "",
    amount: 0
  });  

  const navigate = useNavigate();
  const location = useLocation();

  const handleCheck = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;
    if (checked) {
      setIsChecked([...isChecked, id]);
    } else {
      setIsChecked(isChecked.filter((item) => item !== id));
    }
  };

  const handleCheckAll = () => {
    if (!data) {
      return;
    }
    setCheckedAll(!checkedAll);

    if (checkedAll) {
      setIsChecked([]);
    } else {
      setIsChecked(data.map((li) => li.transactionId));
    }
  };

  const toggleRow = (index: number) => {
    if (openRow === index) {
      setOpenRow(null);
    } else {
      setOpenRow(index);
    }
  };

  const doRefund = async (transaction: any) => {

    setIsBusy(true)

    if (transaction.refundAmount == null || transaction.refundAmount.length < 1) {

      showNotification({
        id: 'invalid-refund-amount',
        message: 'Invalid refund amount',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });

      setIsBusy(false)
      return
    }

    let transactionsToRefund = [transaction];

    try {

      const response = await axiosPrivate.post('/web/refund', {
        transactions: transactionsToRefund
      });
      
      type Response = { 
        success: boolean, 
        transactionNumber: string 
        data: any
      }

      const data: Response[] = response.data.data;
      console.log("response: ", data);

      const failedRefunds = data.filter((itm: Response) => !itm.success);

      if (failedRefunds.length < 1) {
        
        let successMessage = "Refunds successful:\n\n";
        for(let i = 0; i < data.length; i++) {
          successMessage += "RFNDNO " + data[i].data.refundTransactionReferenceNumber + "\n";
        }

        alert(successMessage);
      }
      else {
        
        let errorMessage = "the following transactions failed:\n\n";
        for(let i = 0; i < failedRefunds.length; i++) {
          errorMessage += "TXNNO " + failedRefunds[i].transactionNumber + "\n";
        }

        alert(errorMessage);
      }
      
      navigate(location.pathname, { replace: true });
    }
    catch(error) {
      alert(error);
    }
    
    setIsBusy(false)
  }

  const showRefundWindow = (transaction: any) => {

    setRefundTransaction(transaction);
    setOpened(true);

  }

  const showRefundButton = (transaction: any) => {

    if (auth?.role !== Roles.ADMINISTRATOR) {
      return <div></div>
    }

    return <RefundButton 
      isBusy={isBusy}
      handleClick={() => showRefundWindow(transaction)}
    />
  }

  useDidMountEffect(() => {
    let ignore = false;
    const retrieveNextPage = async () => {
      try {
        const request: RetrieveAllTransactionsRequest = {
          ...transactionDetailsRequest!,
          page: page,
        };
        const response = await axiosPrivate.post('/web/transactions', request);
        const items: BasicTransactionResponseDetails[] = response.data.items;
        if (!ignore) {
          setData(items);
        }
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-next-pagination-data-details',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      }
    };

    retrieveNextPage();

    return () => {
      ignore = true;
    };
  }, [page]);

  return (
    <>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={
          <span style={{
            fontWeight: "bold"
          }}>
            Refund Transaction
          </span>
        }
        centered
      >
        <div style={{
          display: "flex",
          flexDirection: "column",
          height: "200px",
          width: "100%"
        }}>
          <hr style={{
            border: "none",
            borderTop: "1px solid lightgray",
            width: "100%"
          }} />
          <div style={{
            display: "flex",
            justifyContent: "space-between"
          }}>
            <div>Transaction to refund:</div><div>{refundTransaction.transactionId}</div>
          </div>
          <div style={{
            display: "flex",
            justifyContent: "space-between"
          }}>
            <div>Merchant Name:</div><div>{refundTransaction.merchantName}</div>
          </div>
          <div style={{
            display: "flex",
            justifyContent: "space-between"
          }}>
            <div>Transaction Amount:</div><div>
              {formatNumberWithCommasAndDecimals(refundTransaction.amount)}
            </div>
          </div>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "20px"
          }}>
              <div>Refund Amount:</div>
              <input ref={refundAmountRef} type="number" maxLength={12} defaultValue={refundTransaction.amount} />
          </div>
          <div style={{
            marginTop: "30px",
            textAlign: "right"
          }}>
            <Button
              onClick={() => {
                doRefund({
                  ... refundTransaction,
                  refundAmount: refundAmountRef.current?.value
                });
                setOpened(false);
              }}>
              Refund
            </Button>
          </div>
        </div>
      </Modal>
      <UnstyledButton
        color='dimmed'
        onClick={() => {
          setShowTransactionSummary(true);
        }}
        mt='lg'
        data-cy='backButton'
      >
        <Center inline>
          <ArrowLeft size={12} color='#868e96' />
          <Box ml={5}>
            <Text size='sm' color='dimmed'>
              Back
            </Text>
          </Box>
        </Center>
      </UnstyledButton>

      <Title order={2} my='lg'>
        Transaction Details
      </Title>
      <div style={{ flex: 1, margin: 0 }}>
        <Paper
          withBorder
          radius='md'
          shadow='lg'
          style={{
            flex: 1,
            height: '100%',
            padding: '20px 0',
            overflow: 'auto',
          }}
        >
          <Box p='md' style={{ display: "flex" }}>
            {transactionDetailsRequest && (
              <ExportButton
                transactionDetailsRequest={transactionDetailsRequest}
              />
            )}
          </Box>
          <div className='transaction-table'>
            <Table
              highlightOnHover
              sx={{ '& thead tr th': { height: 50 } }}
              horizontalSpacing='md'
            >
              <thead style={{ fontSize: '11px' }}>
                <tr>
                  {
                    <th style={{ width: 30 }}>
                      <input
                        type='checkbox'
                        style={{ cursor: 'pointer' }}
                        onChange={handleCheckAll}
                        checked={checkedAll}
                      />
                    </th>
                  }
                  <th>{tableCols.transactionId}</th>
                  <th>{tableCols.transactionNumber}</th>
                  {/* <th>{tableCols.merchantName}</th> */}
                  <th>{tableCols.branchName}</th>
                  <th>{tableCols.posName}</th>
                  <th>{tableCols.customerName}</th>
                  <th>{tableCols.cardNumber}</th>
                  <th>{tableCols.currency}</th>
                  <th>{tableCols.amount}</th>
                  <th>{tableCols.dateCreated}</th>
                  <th>{tableCols.notes}</th>
                  <th>{tableCols.checkoutTransaction}</th>
                  <th>{tableCols.payoutStatus}</th>
                  <th>{tableCols.refundStatus}</th>
                  {/* ADDITIONAL COLUMNS  START*/}
                  {/* <th>{tableCols.issuingBank}</th> */}
                  {/* <th>{tableCols.bankCountry}</th> */}
                  {/* <th>{tableCols.ipAddress}</th> */}
                  {/* END  */}

                  <th>{tableCols.signatureSource}</th>
                  {/* <th>{tableCols.posEntryMode}</th> */}


                </tr>
              </thead>
              <tbody>
                {data?.map((row, index) => (
                  <>
                    <tr key={row.transactionId} >
                      <td>
                        <input
                          type='checkbox'
                          style={{ cursor: 'pointer' }}
                          id={row.transactionId}
                          checked={isChecked.includes(row.transactionId)}
                          onChange={handleCheck}
                        />
                      </td>
                      <td onClick={() => toggleRow(index)} >
                        <SafeText size='sm' data={row.transactionId} />
                      </td>
                      <td onClick={() => toggleRow(index)}>
                        <Text size='sm'
                          style={{
                            wordBreak: 'break-word',
                            whiteSpace: 'break-spaces',
                            width: '140px'
                          }}>
                          {row.transactionNumber}
                        </Text>
                      </td>
                      {/* <td>
                        <SafeText size='sm' data={row.merchantName} />
                      </td> */}
                      <td onClick={() => toggleRow(index)}>
                        <SafeText
                          size='sm'
                          breakWord={false}
                          data={row.branchName}
                        />
                      </td>
                      <td onClick={() => toggleRow(index)}>
                        <SafeText
                          size='sm'
                          breakWord={false}
                          data={row.posName}
                        />
                      </td>
                      <td onClick={() => toggleRow(index)}>
                        <Text size='sm'
                          style={{
                            wordBreak: 'break-word',
                            whiteSpace: 'break-spaces',
                            width: '100px'
                          }}>
                          {row.customerName}
                        </Text>
                      </td>
                      <td onClick={() => toggleRow(index)}>
                        <SafeText
                          size='sm'
                          breakWord={false}
                          data={row.cardNumber}
                        />
                      </td>
                      <td onClick={() => toggleRow(index)}>
                        <SafeText
                          size='sm'
                          breakWord={false}
                          data={(row.currency)}
                        />
                      </td>
                      <td onClick={() => toggleRow(index)}>
                        <SafeText
                          size='sm'
                          breakWord={false}
                          data={formatNumberWithCommasAndDecimals(row.amount)}
                        />
                      </td>
                      <td onClick={() => toggleRow(index)}>
                        <SafeText
                          size='sm'
                          breakWord={false}
                          data={formatDate3LetterMonth(
                            row.dateCreated.toString()
                          )}
                        />
                      </td>
                      <td onClick={() => toggleRow(index)}>
                        <SafeText size='sm' breakWord={false} data={row.notes} />
                      </td>
                      <td onClick={() => toggleRow(index)}>
                        {row.checkoutTransaction !== undefined ? (
                          <Stack spacing='xs'>
                            <Group noWrap>
                              {row.checkoutTransaction.items
                                .slice(0, 3)
                                .map((product) => {
                                  return <Badge mr={3}>{product.code}</Badge>;
                                })}
                            </Group>
                            {row.checkoutTransaction.items.length > 3 && (
                              <CollapseCode
                                items={row.checkoutTransaction.items.slice(3)}
                              />
                            )}
                          </Stack>
                        ) : (
                          <SafeText size='sm' />
                        )}
                      </td>
                      <td onClick={() => toggleRow(index)}>
                        <SafeText
                          size='sm'
                          breakWord={false}
                          data={row.payoutStatus}
                        />
                      </td>
                      <td onClick={() => toggleRow(index)}>
                        <SafeText
                          size='sm'
                          breakWord={false}
                          data={row.refundStatus}
                        />
                      </td>
                      <td onClick={() => toggleRow(index)}>
                        <SafeText
                          size='sm'
                          breakWord={false}
                          data={row.signatureSource}
                        />
                      </td>
                    </tr>

                    <td colSpan={Object.keys(tableCols).length + 1}>
                      <Collapse in={openRow === index}>
                        <Grid>
                          <Col span={4}>
                            <Col>
                              <Text align='justify' weight={700}>
                                Merchant Name:
                                <Text component="span" weight={400} ml={100}>{row.merchantName ? row.merchantName : 'N/A'}</Text>
                              </Text>
                              <Text color='red' weight={700}>
                                3DS Challenge Provided:
                                <Text component="span" weight={400} ml={100}>{row.threeDSChallengeProvided ? '1' : '0'}</Text>
                              </Text>
                              <Text color='red' align='justify' weight={700}>
                                3DS ECI:
                                <Text component="span" weight={400} ml={222}>{row.threeDSECI ? row.threeDSECI : 'N/A'}</Text>
                              </Text>
                              <Text color='red' weight={700}>
                                3DS Authentication Status:
                                <Text component="span" weight={400} ml={82}>{row.threeDSAuthenticationStatus ? row.threeDSAuthenticationStatus : 'N/A'}</Text>
                              </Text>
                              <Text align='justify' weight={700}>
                                DETAILED NOTES:  <Text component="span" weight={400}>{row.notes ? row.notes : 'N/A'}</Text>
                              </Text>
                            </Col>
                          </Col>
                          <Col span={3} offset={row.reference ? -1.9 : -2.1}>
                            <Col>
                              <Text align='justify' weight={700}>
                                Industry MID:
                                <Text component="span" weight={400} ml={50}>{row.industryMid ? row.industryMid : 'N/A'}</Text>
                              </Text>
                              <Text weight={700}>
                                POS Entry:
                                <Text component="span" weight={400} ml={76}>{row.posEntryMode ? row.posEntryMode : 'N/A'}</Text>
                              </Text>
                              <Text align='justify' weight={700}>
                                Reference:
                                <Text component="span" weight={400} ml={76}>{row.reference ? row.reference : 'N/A'}</Text>
                              </Text>
                              <Text weight={700}>
                                Auth Code:
                                <Text component="span" weight={400} ml={72}>{row.authNumber ? row.authNumber : 'N/A'}</Text>
                              </Text>
                            </Col>
                          </Col>
                          <Col span={3} offset={row.reference ? -1 : -1.3}>
                            <Col>
                              <Text align='justify' weight={700}>
                                Issuing Bank:
                                <Text component="span" weight={400} ml={125}>{row.issuingBank ? row.issuingBank : 'N/A'}</Text>
                              </Text>
                              <Text weight={700}>
                                Country:
                                <Text component="span" weight={400} ml={160}>{row.bankCountry ? row.bankCountry : 'N/A'}</Text>
                              </Text>
                              <Text align='justify' weight={700}>
                                IP Address:
                                <Text
                                  component="span"
                                  color='gray'
                                  weight={400}
                                  ml={row.ipAddress ? Math.max(60, 110 - (row.ipAddress.length - 9) * 10) : 60}
                                >
                                  {row.ipAddress ? row.ipAddress : 'XXX.XXX.XXX.XXX'}
                                </Text>
                              </Text>
                              {showRefundButton(row)}
                            </Col>
                          </Col>
                        </Grid>
                      </Collapse>
                    </td>


                  </>
                ))}
              </tbody>
            </Table >

            {data && data.length > 0 && (
              <Pagination
                total={paginationData?.totalPages || 1}
                onChange={(value: number) => setPage(value)}
                page={page}
                position='center'
                p='md'
                spacing='md'
              />
            )}
          </div>
        </Paper >
      </div >
    </>
  );
}

export default AdminTransactionsDetails;
